import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { formatDisplayQty } from '#/utils/formatNumber';
import EntityStatusBadge from '#/modules/commonComponents/table/EntityStatusBadge';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import ProductCommandBar from '#/modules/crmMgmt/product/components/ProductCommandBar';
import { formatProductStatusLabel } from '#/modules/crmMgmt/product/helper/productStatus';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { formatDateTimeOrDash } from '#/utils/date';

function displayOrDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function HistoryField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">{value}</p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function resolveBaseUnitName(baseUnit) {
  if (!baseUnit) {
    return '—';
  }
  if (typeof baseUnit === 'string') {
    return displayOrDash(baseUnit);
  }
  return displayOrDash(baseUnit.name ?? baseUnit.code);
}

const CELL_CLASS = 'px-2 py-1.5 border-r border-slate-200 last:border-r-0';

function ProductMainInfoCard({ detail, customerLabel, actions = null }) {
  const status = detail?.status;

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {displayOrDash(detail?.productNo)}
              </h1>
              {status ? (
                <EntityStatusBadge status={status} label={formatProductStatusLabel(status)} />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {displayOrDash(detail?.name)}
            </p>
          </div>
          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {actions}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
            'sm:grid-cols-2 md:grid-cols-4',
          )}
        >
          <HistoryField
            label="建立"
            value={formatDateTimeOrDash(
              detail?.record?.createdAt ?? detail?.createdAt,
            )}
          />
          <HistoryField
            label="修改"
            value={formatDateTimeOrDash(
              detail?.record?.updatedAt ?? detail?.updatedAt,
            )}
          />
          <HistoryField label="銷售單位" value={displayOrDash(detail?.salesUnitName)} />
          <HistoryField label="客戶" value={displayOrDash(customerLabel)} />
          <HistoryField label="客戶料號" value={displayOrDash(detail?.customerProductNo)} />
          <div className="sm:col-span-2 md:col-span-4">
            <p className="text-xs text-gray-500">備註</p>
            <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
              {displayOrDash(detail?.remark)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

ProductMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  customerLabel: PropTypes.string,
  actions: PropTypes.node,
};

function ProductMaterialsReadonlySection({ materials = [] }) {
  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <h2 className="form-block__title">物料</h2>
        {materials.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">尚無物料</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[30rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  <th className={cn(CELL_CLASS, 'font-medium')}>物料</th>
                  <th className={cn(CELL_CLASS, 'font-medium')}>用量</th>
                  <th className={cn(CELL_CLASS, 'font-medium')}>單位</th>
                  <th className={cn(CELL_CLASS, 'font-medium')}>備註</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((row, index) => {
                  const material = row?.material ?? {};
                  return (
                    <tr
                      key={String(row?.id ?? `material-${index}`)}
                      className="border-t border-slate-200"
                    >
                      <td className={cn(CELL_CLASS, 'min-w-0')}>
                        <MaterialNameStack
                          name={material.name}
                          code={material.mtrNo}
                        />
                      </td>
                      <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                        {formatDisplayQty(row?.qtyPerProduct)}
                      </td>
                      <td className={CELL_CLASS}>
                        {resolveBaseUnitName(material.baseUnit)}
                      </td>
                      <td className={CELL_CLASS}>{displayOrDash(row?.remark)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

ProductMaterialsReadonlySection.propTypes = {
  materials: PropTypes.arrayOf(PropTypes.object),
};

function ProductExtraItemsReadonlySection({ extraItems = [] }) {
  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <h2 className="form-block__title">附加項目</h2>
        {extraItems.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">尚無附加項目</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[28rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  <th className={cn(CELL_CLASS, 'font-medium')}>項目名稱</th>
                  <th className={cn(CELL_CLASS, 'font-medium')}>數量</th>
                  <th className={cn(CELL_CLASS, 'font-medium')}>單位</th>
                  <th className={cn(CELL_CLASS, 'font-medium')}>備註</th>
                </tr>
              </thead>
              <tbody>
                {extraItems.map((row, index) => (
                  <tr
                    key={String(row?.id ?? `extra-${index}`)}
                    className="border-t border-slate-200"
                  >
                    <td className={cn(CELL_CLASS, 'min-w-0')}>
                      <TruncatedTextWithTooltip text={displayOrDash(row?.itemName)} />
                    </td>
                    <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                      {formatDisplayQty(row?.qtyPerProduct)}
                    </td>
                    <td className={CELL_CLASS}>{displayOrDash(row?.unitName)}</td>
                    <td className={CELL_CLASS}>{displayOrDash(row?.remark)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

ProductExtraItemsReadonlySection.propTypes = {
  extraItems: PropTypes.arrayOf(PropTypes.object),
};

const ProductBlock = ({
  detail,
  onRefetch,
  showActions = false,
  customerLabel,
}) => (
  <div className="space-y-4">
    <ProductMainInfoCard
      detail={detail}
      customerLabel={customerLabel}
      actions={
        showActions ? (
          <ProductCommandBar
            key={`${detail.id}-${detail.record?.updatedAt ?? detail.updatedAt ?? ''}`}
            detail={detail}
            onRefetch={onRefetch}
          />
        ) : null
      }
    />
    <ProductMaterialsReadonlySection materials={detail?.materials ?? []} />
    <ProductExtraItemsReadonlySection extraItems={detail?.extraItems ?? []} />
  </div>
);

ProductBlock.propTypes = {
  detail: PropTypes.object.isRequired,
  onRefetch: PropTypes.func,
  showActions: PropTypes.bool,
  customerLabel: PropTypes.string,
};

export default ProductBlock;
